import {
  ComplianceItem,
  ComplianceStatus,
  CommunitySchema,
  DSCommunitySchema,
  PreferenceSchema,
  ResourceSchema,
  ResourceType,
} from '../data/types';
import { isKitchenOffered } from './helpers/dashboardStats';

export const COMPLIANCE_ONBOARDING_STORAGE_PREFIX = 'homecook_compliance_onboarding_';

/** Resource types shown in the setup checklist dropdown. */
export const COMPLIANCE_SUPPORTED_RESOURCE_TYPES: ResourceType[] = [
  'kitchen',
  'documentation',
  'farm',
];

export type ComplianceLocationFields = {
  city: string;
  county: string;
  state: string;
  zipcode: string;
};

export type ComplianceResourceOption = {
  id: ResourceType;
  label: string;
  createPath: string;
  createLabel: string;
};

export const COMPLIANCE_RESOURCE_OPTIONS: ComplianceResourceOption[] = [
  {
    id: 'kitchen',
    label: 'Kitchen Spaces',
    createPath: '/resources/kitchen?create=1',
    createLabel: 'Create Kitchen Space',
  },
  {
    id: 'documentation',
    label: 'Documentation',
    createPath: '/resources/documentation?create=1',
    createLabel: 'Create Documentation',
  },
  {
    id: 'farm',
    label: 'Fresh Food',
    createPath: '/resources/farm?create=1',
    createLabel: 'Create Fresh Food Resource',
  },
];

/**
 * Filter checklist dropdown types from Settings → Resources to Offer.
 * Kitchen stays offered unless explicitly turned off; documentation and farm require true.
 */
export const getOfferedComplianceResourceOptions = (
  preferences?: PreferenceSchema
): ComplianceResourceOption[] => {
  const offeredByType: Partial<Record<ResourceType, boolean>> = {
    kitchen: isKitchenOffered(preferences),
    documentation: Boolean(preferences?.documentation),
    farm: Boolean(preferences?.farm),
  };

  return COMPLIANCE_RESOURCE_OPTIONS.filter((option) => offeredByType[option.id]);
};

/** Whether the resource type appears in the setup checklist dropdown. */
export const isComplianceSupportedResourceType = (
  resourceType?: ResourceType | null
): boolean =>
  Boolean(resourceType && COMPLIANCE_SUPPORTED_RESOURCE_TYPES.includes(resourceType));

/** Kitchen resources receive location-scoped requirements and partner action tracking. */
export const tracksKitchenComplianceRequirements = (
  resourceType?: ResourceType | null
): boolean => resourceType === 'kitchen';

/** Partner-facing action counts apply to kitchen uploads/renewals only. */
export const showsComplianceActionNeeded = (
  resourceType?: ResourceType | null
): boolean => tracksKitchenComplianceRequirements(resourceType);

/** Normalize location fields that may be stored as a string or string[]. */
export const normalizeComplianceField = (
  value: string | string[] | undefined
): string => {
  if (Array.isArray(value)) {
    return value[0]?.trim() || '';
  }
  return value?.trim() || '';
};

/** Resolve jurisdiction fields from resource first, then community. */
export const resolveComplianceLocation = (
  community?: DSCommunitySchema | null,
  resource?: ResourceSchema | null
): ComplianceLocationFields => ({
  city: normalizeComplianceField(resource?.city || community?.city),
  county: normalizeComplianceField(resource?.county || community?.county),
  state: normalizeComplianceField(resource?.state || community?.state),
  zipcode: normalizeComplianceField(resource?.zipcode || community?.zipcode),
});

/** Returns true when city, state, and zipcode are present for regulatory lookup. */
export const hasCompleteComplianceLocation = (
  community?: DSCommunitySchema | null,
  resource?: ResourceSchema | null
): boolean => {
  const location = resolveComplianceLocation(community, resource);
  return Boolean(location.city && location.state && location.zipcode);
};

/** Map wizard/form location values onto CommunitySchema update shape. */
export const toCommunityLocationUpdate = (
  location: ComplianceLocationFields
): Pick<CommunitySchema, 'city' | 'county' | 'state' | 'zipcode'> => ({
  city: location.city,
  county: location.county,
  state: location.state ? [location.state] : [],
  zipcode: location.zipcode,
});

/** Human-readable location label for resource cards. */
export const formatComplianceLocationLabel = (resource: ResourceSchema): string => {
  const parts = [
    normalizeComplianceField(resource.city),
    normalizeComplianceField(resource.state),
    normalizeComplianceField(resource.zipcode),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'Location not set';
};

/** Items that still need partner action. */
export const complianceItemNeedsAction = (status: ComplianceStatus): boolean =>
  status === 'pending' || status === 'expired';

/** Count partner-actionable items for a kitchen resource. */
export const countActionNeeded = (
  items: ComplianceItem[],
  resourceType?: ResourceType | null
): number => {
  if (!showsComplianceActionNeeded(resourceType)) {
    return 0;
  }
  return items.filter((item) => complianceItemNeedsAction(item.status)).length;
};

/** Keep items scoped to the selected resource id and type. */
export const filterComplianceItemsForResource = (
  items: ComplianceItem[],
  resourceId: string,
  resourceType: ResourceType
): ComplianceItem[] =>
  items.filter(
    (item) =>
      item.resource_id === resourceId &&
      (item.resource_type || 'kitchen') === resourceType
  );

/** Whether onboarding wizard should display for this community. */
export const shouldShowComplianceOnboarding = (
  communityId: string,
  community?: DSCommunitySchema | null
): boolean => {
  if (!communityId) {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const completed = window.localStorage.getItem(
    `${COMPLIANCE_ONBOARDING_STORAGE_PREFIX}${communityId}`
  );
  if (completed === 'complete') {
    return false;
  }

  if (!isKitchenOffered(community?.preferences)) {
    return false;
  }

  return !hasCompleteComplianceLocation(community);
};

/** Persist onboarding completion for a community. */
export const markComplianceOnboardingComplete = (communityId: string): void => {
  if (typeof window === 'undefined' || !communityId) {
    return;
  }
  window.localStorage.setItem(
    `${COMPLIANCE_ONBOARDING_STORAGE_PREFIX}${communityId}`,
    'complete'
  );
};

/** Stable location key — must match backend `location_snapshot()`. */
export const buildComplianceLocationSnapshot = (
  location: ComplianceLocationFields
): string =>
  [
    location.city.toLowerCase(),
    location.county.toLowerCase(),
    location.state.toLowerCase(),
    location.zipcode.slice(0, 5),
  ].join('|');

/** True when persisted items were generated for a different jurisdiction. */
export const complianceItemsNeedLocationRefresh = (
  items: ComplianceItem[],
  location: ComplianceLocationFields
): boolean => {
  if (items.length === 0) {
    return false;
  }
  const snapshot = buildComplianceLocationSnapshot(location);
  return items.some(
    (item) => item.location_snapshot && item.location_snapshot !== snapshot
  );
};

/** Filter resources by type. */
export const filterResourcesByType = (
  resources: ResourceSchema[] | undefined,
  resourceType: ResourceType
): ResourceSchema[] =>
  (resources || []).filter((resource) => resource.resource_type === resourceType);
